/**
 * ShareToClaudeDialog
 *
 * "Share to Claude" handoff dialog. One trigger button on each detail page
 * (issue + investigation). When opened, lazily mints a share token via the
 * backend, then presents three sections in descending visual hierarchy:
 *
 *   1. CLAUDE CLI       (primary)   — `claude "$(curl -s <prompt_url>)"`
 *   2. SHARE LINK       (secondary) — copy-able URL + expiry + revoke link
 *   3. MCP CONFIG       (collapsed) — JSON snippet + CLI one-liner + PAT CTA
 *
 * Aesthetic mirrors the rest of the retro / JetBrains Mono UI: amber accent,
 * uppercase monospace headings, inline subtle glyphs. Spacing on the 8dp
 * grid. Every icon-only button has aria-label. Touch targets ≥ 44px. Copy
 * micro-interaction uses an aria-live polite region for screen readers
 * instead of a toast. Motion respects `prefers-reduced-motion`.
 *
 * Open / close lifecycle is owned by the parent (controlled). The dialog
 * caches the mint response in component state once obtained so re-opening
 * the same instance is free; an explicit "regenerate" button revokes the
 * current token and mints a fresh one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useReducedMotion } from '@/hooks/useReducedMotion';

import { CopyableField, SectionHeader } from '@/components/CopyableField';
import { ApiError, share as shareApi } from '@/helpers/ApiHelper';
import type { ShareMintResponse } from '@/types/share';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShareToClaudeDialogProps {
  /** Controlled-open. The trigger sets this true; the dialog calls onClose. */
  open: boolean;
  onClose: () => void;
  /** Determines which mint endpoint to hit. */
  target: { type: 'analysis'; analysisId: string } | { type: 'investigation'; investigationId: string };
}

type DialogState =
  | { kind: 'idle' }
  | { kind: 'minting' }
  | { kind: 'ready'; share: ShareMintResponse; bodyPreview?: string }
  | { kind: 'error'; message: string };

// ---------------------------------------------------------------------------
// Shell-safe escape for the inline-body fallback CLI command
// ---------------------------------------------------------------------------

/**
 * Escape a string for safe insertion into a double-quoted bash argument.
 * `claude "<this>"` — we replace `\`, `"`, `$`, and backticks. Newlines stay
 * as literal newlines (bash allows multi-line strings inside `"..."`).
 */
function escapeForDoubleQuotedShellArg(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\$/g, '\\$')
    .replace(/`/g, '\\`');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShareToClaudeDialog({ open, onClose, target }: ShareToClaudeDialogProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const reduceMotion = useReducedMotion();

  const [state, setState] = useState<DialogState>({ kind: 'idle' });
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [useInlineBody, setUseInlineBody] = useState(false);
  const [revoking, setRevoking] = useState(false);
  // Per-section "just copied" flag drives the icon swap; keyed by section id
  // so multiple copies don't interfere with each other.
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  // Focus target for the primary copy button — autofocused on open per
  // ui-ux-pro-max focus-management.
  const primaryCopyRef = useRef<HTMLButtonElement | null>(null);

  // ---------- mint on first open ----------

  const mintFresh = useCallback(async () => {
    setState({ kind: 'minting' });
    try {
      const resp =
        target.type === 'analysis'
          ? await shareApi.mintForAnalysis(target.analysisId)
          : await shareApi.mintForInvestigation(target.investigationId);
      // Pre-fetch the body — only when the user toggles "inline body" do we
      // surface it, but pre-fetching means the toggle is instant.
      let bodyPreview: string | undefined;
      try {
        const r = await fetch(resp.prompt_url);
        if (r.ok) bodyPreview = await r.text();
      } catch {
        // Inline-body fetch is best-effort; the `$(curl …)` form still works.
      }
      setState({ kind: 'ready', share: resp, bodyPreview });
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : 'Could not mint share link';
      setState({ kind: 'error', message: detail });
    }
  }, [target]);

  useEffect(() => {
    if (open && state.kind === 'idle') {
      void mintFresh();
    }
  }, [open, state.kind, mintFresh]);

  // Reset when the dialog closes so re-opening on a different target re-mints.
  // We DON'T reset when same target re-opens — the existing mint is cached.
  useEffect(() => {
    if (!open) {
      setCopyFeedback(null);
      setCopiedKey(null);
    }
  }, [open]);

  // ---------- copy helper with success-feedback micro-interaction ----------

  const handleCopy = useCallback(
    async (text: string, key: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedKey(key);
        setCopyFeedback(`${label} copied`);
        const holdMs = reduceMotion ? 600 : 1500;
        window.setTimeout(() => {
          setCopiedKey((curr) => (curr === key ? null : curr));
        }, holdMs);
      } catch {
        setCopyFeedback(`Could not copy ${label.toLowerCase()}`);
      }
    },
    [reduceMotion],
  );

  // ---------- revoke + regenerate ----------

  const handleRegenerate = useCallback(async () => {
    if (state.kind !== 'ready') return;
    setRevoking(true);
    try {
      try {
        await shareApi.revoke(state.share.token);
      } catch {
        // If revoke fails (already expired etc.), still proceed to mint.
      }
      await mintFresh();
    } finally {
      setRevoking(false);
    }
  }, [state, mintFresh]);

  // ---------- derived CLI commands ----------

  const claudeCliCommand =
    state.kind === 'ready'
      ? `claude "$(curl -s ${state.share.prompt_url})"`
      : '';

  const inlineCliCommand =
    state.kind === 'ready' && state.bodyPreview
      ? `claude "${escapeForDoubleQuotedShellArg(state.bodyPreview)}"`
      : '';

  // MCP URL points at the backend, not window.location.origin: the dev Vite
  // proxy only forwards /api/*, and in prod the frontend (sherlock.*) and
  // backend (sherlock-api.*) are separate origins. Fall back to the dev
  // backend port when VITE_API_URL isn't configured.
  const mcpBase =
    (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:8000';
  const mcpUrl = `${mcpBase}/mcp/sse`;

  const mcpConfigJson = state.kind === 'ready'
    ? JSON.stringify(
        {
          mcpServers: {
            sherlock: {
              transport: 'sse',
              url: mcpUrl,
              headers: { Authorization: 'Bearer <YOUR_PAT>' },
            },
          },
        },
        null,
        2,
      )
    : '';

  const mcpCliSnippet = state.kind === 'ready'
    ? `claude mcp add --transport sse sherlock ${mcpUrl} --header "Authorization: Bearer <YOUR_PAT>"`
    : '';

  // ---------- expiry caption ----------

  const expiryCaption = state.kind === 'ready' ? formatRelativeExpiry(state.share.expires_at) : '';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // MUI's default scale+fade — disable when prefers-reduced-motion.
      transitionDuration={reduceMotion ? 0 : undefined}
      // Trap focus to the primary copy button on open.
      onTransitionEnter={() => {
        if (primaryCopyRef.current) primaryCopyRef.current.focus();
      }}
      aria-labelledby="share-to-claude-title"
    >
      {/* Polite aria-live region for copy / revoke status — visually hidden but read by screen readers. */}
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {copyFeedback}
      </Box>

      <DialogTitle
        id="share-to-claude-title"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontSize: '0.875rem',
          fontWeight: 700,
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <span>▸ SHARE TO CLAUDE</span>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="large"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {state.kind === 'error' && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" role="alert">
              {state.message}
            </Alert>
            <Button onClick={() => void mintFresh()} sx={{ mt: 2 }}>
              Try again
            </Button>
          </Box>
        )}

        {state.kind !== 'error' && (
          <Stack divider={<Box sx={{ height: '1px', bgcolor: 'divider' }} />}>
            {/* ============================================================ */}
            {/* SECTION 1: CLAUDE CLI — primary, highest visual weight       */}
            {/* ============================================================ */}
            <Box sx={{ p: 3 }}>
              <SectionHeader
                title="CLAUDE CLI"
                subtitle="Paste this in your repo terminal."
                primary
              />

              {state.kind !== 'ready' && (
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, mt: 2 }} />
              )}

              {state.kind === 'ready' && (
                <>
                  <Box sx={{ mt: 2 }}>
                    <CopyableField
                      value={useInlineBody && inlineCliCommand ? inlineCliCommand : claudeCliCommand}
                      ariaLabel={useInlineBody ? 'Inline claude command' : 'Claude CLI command'}
                      onCopy={() =>
                        handleCopy(
                          useInlineBody && inlineCliCommand ? inlineCliCommand : claudeCliCommand,
                          'cli',
                          useInlineBody ? 'Inline command' : 'Claude command',
                        )
                      }
                      copied={copiedKey === 'cli'}
                      copyButtonRef={primaryCopyRef}
                      multiline={useInlineBody}
                      monospace
                    />
                  </Box>
                  {inlineCliCommand && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setUseInlineBody((v) => !v)}
                      sx={{
                        mt: 1,
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: 11,
                        textTransform: 'none',
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      {useInlineBody ? '› use $(curl …) form instead' : '› use inline body instead'}
                    </Button>
                  )}
                </>
              )}
            </Box>

            {/* ============================================================ */}
            {/* SECTION 2: SHARE LINK — secondary                            */}
            {/* ============================================================ */}
            <Box sx={{ p: 3 }}>
              <SectionHeader title="SHARE LINK" subtitle="Token-only; expires automatically." />

              {state.kind !== 'ready' && (
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, mt: 2 }} />
              )}

              {state.kind === 'ready' && (
                <>
                  <Box sx={{ mt: 2 }}>
                    <CopyableField
                      value={state.share.share_url}
                      ariaLabel="Share URL"
                      onCopy={() =>
                        handleCopy(state.share.share_url, 'url', 'Share URL')
                      }
                      copied={copiedKey === 'url'}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1.5, gap: 2, flexWrap: 'wrap' }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontVariantNumeric: 'tabular-nums',
                        color: 'text.secondary',
                      }}
                    >
                      {expiryCaption}
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Button
                        onClick={() => void handleRegenerate()}
                        disabled={revoking}
                        size="small"
                        startIcon={
                          revoking ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon fontSize="small" />
                        }
                        sx={{
                          minHeight: 44,
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: 11,
                          textTransform: 'none',
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        regenerate
                      </Button>
                      <Button
                        onClick={async () => {
                          if (state.kind !== 'ready') return;
                          try {
                            await shareApi.revoke(state.share.token);
                            setCopyFeedback('Share revoked');
                            onClose();
                          } catch (err) {
                            const detail = err instanceof ApiError ? err.detail : 'Revoke failed';
                            setCopyFeedback(detail);
                          }
                        }}
                        size="small"
                        aria-label="Revoke share link"
                        sx={{
                          minHeight: 44,
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: 11,
                          textTransform: 'none',
                          color: 'error.main',
                          '&:hover': {
                            color: 'error.dark',
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        revoke
                      </Button>
                    </Stack>
                  </Stack>
                </>
              )}
            </Box>

            {/* ============================================================ */}
            {/* SECTION 3: MCP CONFIG — collapsed; JSON snippet + CLI snippet */}
            {/* ============================================================ */}
            <Box sx={{ px: 3, py: 1 }}>
              <Accordion
                disableGutters
                square
                elevation={0}
                sx={{
                  background: 'transparent',
                  border: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  sx={{
                    px: 0,
                    minHeight: 44,
                    '& .MuiAccordionSummary-content': { my: 0 },
                  }}
                >
                  <SectionHeader title="MCP CONFIG" subtitle="for Claude Code" />
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 1, pb: 2 }}>
                  {state.kind !== 'ready' && (
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                  )}
                  {state.kind === 'ready' && (
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            mb: 0.5,
                            display: 'block',
                          }}
                        >
                          claude.json snippet
                        </Typography>
                        <CopyableField
                          value={mcpConfigJson}
                          ariaLabel="MCP JSON config snippet"
                          onCopy={() => handleCopy(mcpConfigJson, 'mcp-json', 'MCP config')}
                          copied={copiedKey === 'mcp-json'}
                          multiline
                          monospace
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            mb: 0.5,
                            display: 'block',
                          }}
                        >
                          one-shot command
                        </Typography>
                        <CopyableField
                          value={mcpCliSnippet}
                          ariaLabel="MCP one-shot CLI command"
                          onCopy={() => handleCopy(mcpCliSnippet, 'mcp-cli', 'MCP CLI command')}
                          copied={copiedKey === 'mcp-cli'}
                          monospace
                        />
                      </Box>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          onClose();
                          navigate('/settings/pats?new=1');
                        }}
                        sx={{
                          minHeight: 44,
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: 12,
                          textTransform: 'none',
                        }}
                      >
                        generate personal access token →
                      </Button>
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Expiry formatting — "expires in 7 days" / "expires today" / "expired"
// ---------------------------------------------------------------------------

function formatRelativeExpiry(iso: string): string {
  const expires = new Date(iso).getTime();
  const now = Date.now();
  const ms = expires - now;
  if (ms <= 0) return 'expired';
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(ms / day);
  if (days === 0) {
    const hours = Math.max(1, Math.floor(ms / (60 * 60 * 1000)));
    return `expires in ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `expires in ${days} day${days === 1 ? '' : 's'}`;
}
